/**
 * duckdb DS 상세 페이지에서 부모 Tableau 자원의 액션 (통합/스케줄/Skill) 을 미러링하는 박스.
 *
 * 부모 자원이 있을 때만 (= duckdb 가 통합 산출물일 때만) 박스를 그린다.
 * 직접 만든 duckdb 면 null 반환 → 일반 SQL 데이터소스 detail 만 표시.
 */
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { TableauResource } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  CalendarClock,
  ExternalLink,
  Loader2,
  Play,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { TableauScheduleDialog } from './TableauScheduleDialog';

interface Props {
  /** duckdb 데이터소스 id (= URL 의 :id). */
  duckdbDataSourceId: string;
  /** 자원+duckdb 함께 삭제 후 호출. 일반적으로 /admin/data-sources 로 navigate. */
  onDeleted?: () => void;
}

export function DuckdbTableauSourcePanel({ duckdbDataSourceId, onDeleted }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'duckdb-tableau-source', duckdbDataSourceId],
    queryFn: () => api.dataSourceTableauSource(duckdbDataSourceId),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.resource?.integrateStartedAt) return 3000;
      return false;
    },
  });

  const integrateMutation = useMutation({
    mutationFn: () => {
      if (!data?.sourceTableauDataSourceId || !data.resource)
        throw new Error('부모 Tableau 자원 정보 없음');
      // 이미 통합된 자원의 재실행. live 분류인데 duckdb 가 있는 경우(과거 "Live 통합 시도"로
      // 성공)는 라우트 가드를 통과하도록 attemptLive 를 함께 보낸다.
      const attemptLive = data.resource.extractType === 'live';
      return api.tableauResourceIntegrate(
        data.sourceTableauDataSourceId,
        data.resource.id,
        attemptLive ? { attemptLive: true } : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'duckdb-tableau-source', duckdbDataSourceId] });
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: (cron: string | null) => {
      if (!data?.sourceTableauDataSourceId || !data.resource)
        throw new Error('부모 Tableau 자원 정보 없음');
      return api.tableauResourceSchedule(data.sourceTableauDataSourceId, data.resource.id, {
        cron,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'duckdb-tableau-source', duckdbDataSourceId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!data?.sourceTableauDataSourceId || !data.resource)
        throw new Error('부모 Tableau 자원 정보 없음');
      return api.tableauResourceDelete(data.sourceTableauDataSourceId, data.resource.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'data-sources'] });
      onDeleted?.();
    },
  });

  // 직접 만든 duckdb (resource null + skill null) — 박스 자체를 안 그림.
  if (isLoading) return null;
  if (error || !data || (!data.resource && !data.autoSkill)) return null;

  const r: TableauResource | null = data.resource;
  const sourceTableauId = data.sourceTableauDataSourceId;

  return (
    <>
      <section className="rounded-2xl border border-border bg-surface p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-foreground">Tableau 자원</h3>
            {r ? (
              <>
                <div className="text-[11px] text-foreground-muted truncate mt-0.5">
                  {r.projectPath || '/'}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-base font-medium truncate">{r.name}</span>
                  <ResourceTypeBadge type={r.resourceType} />
                  <ExtractBadge type={r.extractType} />
                </div>
                <ResourceMeta resource={r} />
              </>
            ) : (
              <p className="text-sm text-foreground-muted mt-1">
                자동 생성된 Skill 만 연결돼 있고 부모 자원 정보가 없습니다.
              </p>
            )}
          </div>
        </div>

        {r ? <IntegrateStatusBox resource={r} /> : null}

        <div className="flex flex-wrap gap-2">
          {r && sourceTableauId ? (
            <>
              <Button
                type="button"
                onClick={() => integrateMutation.mutate()}
                disabled={
                  (r.extractType !== 'extract' &&
                    r.extractType !== 'flow_output' &&
                    !r.duckdbDataSourceId) ||
                  integrateMutation.isPending ||
                  !!r.integrateStartedAt
                }
                title={
                  r.extractType === 'live' && !r.duckdbDataSourceId
                    ? '라이브 방식은 지원하지 않습니다'
                    : r.integrateStartedAt
                      ? '이미 통합 진행 중입니다'
                      : undefined
                }
              >
                {integrateMutation.isPending || r.integrateStartedAt ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Play className="size-4" />
                )}
                통합
              </Button>
              <Button type="button" variant="secondary" onClick={() => setScheduleOpen(true)}>
                <CalendarClock className="size-4" />
                스케줄
              </Button>
            </>
          ) : null}

          {data.autoSkill ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                navigate({
                  to: '/admin/skills/$id',
                  params: { id: data.autoSkill?.id ?? '' },
                  search: { dataSourceId: undefined },
                })
              }
            >
              <Sparkles className="size-4" />
              Skill 열기
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                navigate({
                  to: '/admin/skills/$id',
                  params: { id: 'new' },
                  search: { dataSourceId: duckdbDataSourceId },
                })
              }
            >
              <Sparkles className="size-4" />
              Skill 만들기
            </Button>
          )}

          {sourceTableauId ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                navigate({
                  to: '/admin/data-sources/$id',
                  params: { id: sourceTableauId },
                })
              }
              title="자원 목록 (부모 Tableau 데이터소스 페이지)"
            >
              <ExternalLink className="size-4" />
              자원 페이지 열기
            </Button>
          ) : null}

          {r && sourceTableauId ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (
                  window.confirm(
                    `"${r.name}" 자원과 통합으로 만든 DuckDB 데이터소스를 함께 삭제할까요?`,
                  )
                ) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="size-4" />
              삭제
            </Button>
          ) : null}
        </div>
      </section>

      {r && sourceTableauId ? (
        <TableauScheduleDialog
          open={scheduleOpen}
          initial={r.scheduleCron}
          onClose={() => setScheduleOpen(false)}
          onSave={(cron) => scheduleMutation.mutate(cron)}
        />
      ) : null}
    </>
  );
}

function ResourceMeta({ resource }: { resource: TableauResource }) {
  const items: string[] = [];
  if (resource.lastIntegratedAt) {
    items.push(`마지막 통합: ${formatRelative(resource.lastIntegratedAt)}`);
  } else {
    items.push('미통합');
  }
  if (resource.avgDurationMs !== null && resource.avgDurationMs !== undefined) {
    items.push(`평균 ${(resource.avgDurationMs / 1000).toFixed(1)}s`);
  }
  if (resource.scheduleCron) {
    items.push(`스케줄: ${resource.scheduleCron}`);
  }
  return (
    <div className="mt-1 text-[11px] text-foreground-muted flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {items.map((item) => (
        <span key={item}>{item}</span>
      ))}
    </div>
  );
}

function IntegrateStatusBox({ resource }: { resource: TableauResource }) {
  if (resource.integrateStartedAt) {
    const elapsed = Math.max(
      1,
      Math.floor((Date.now() - new Date(resource.integrateStartedAt).getTime()) / 1000),
    );
    return (
      <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-sm space-y-1">
        <div className="flex items-center gap-2 text-accent">
          <Loader2 className="size-3.5 animate-spin shrink-0" />
          <span className="font-medium">{resource.integrateProgressItem ?? '진행 중…'}</span>
        </div>
        <p className="text-xs text-foreground-muted pl-5">{elapsed}초 경과</p>
      </div>
    );
  }
  if (resource.lastIntegrateError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm space-y-2">
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">마지막 통합 실패</p>
            <p className="mt-1 text-xs whitespace-pre-wrap break-words">
              {resource.lastIntegrateError}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

function ResourceTypeBadge({ type }: { type: 'datasource' | 'workbook' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 h-4 rounded-sm text-[10px] uppercase tracking-wider',
        'bg-surface-muted text-foreground-muted',
      )}
    >
      {type === 'workbook' ? '통합문서' : '데이터원본'}
    </span>
  );
}

function ExtractBadge({ type }: { type: 'extract' | 'live' | 'flow_output' }) {
  const isIntegrable = type === 'extract' || type === 'flow_output';
  const label = type === 'flow_output' ? '흐름 출력' : type;
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 h-4 rounded-sm text-[10px] tracking-wider',
        type === 'flow_output' ? '' : 'uppercase',
        isIntegrable ? 'bg-accent/15 text-accent' : 'bg-surface-muted text-foreground-muted',
      )}
      title={
        type === 'live'
          ? '라이브 자원은 통합 불가'
          : type === 'flow_output'
            ? 'Tableau Prep 흐름 출력 — 통합 가능'
            : undefined
      }
    >
      {label}
    </span>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.max(1, Math.floor(diff / 1000))}초 전`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}분 전`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}시간 전`;
  return `${Math.floor(diff / 86_400_000)}일 전`;
}

void ApiError; // referenced indirectly
